import { Component, Injector, Input, OnInit, ChangeDetectorRef, effect } from '@angular/core';
import { BaseModalComponent, EnumValues } from '@card-software/library';
import { Invoice } from '../../../models/invoice/invoice';
import { ShipmentProxyService } from '../../../proxies/order/shipment.proxy.service';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { LotsService } from '../../../services/lots.service';
import _ from 'lodash';
import { LotItem } from '../../../models/inventory/lot-item';
import { LotExcelImportContext, LotExcelImportPreview } from '../../../models/inventory/lot-excel-import';
import { InventoryLotProxyService } from '../../../proxies/inventory/inventory-lot.proxy.service';
import { AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { EnumService } from '../../../services/enum.service';
import { Business } from '../../../models/finance/business';
import { ItemMasterProxyService } from '../../../proxies/item/item-master.proxy.service';
import { ItemMaster, ItemSearchCommand } from '../../../models/item/item-master/item-master';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { YearWeek } from '../../../models/order/work-order';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { BusinessSearchCommand } from '../../../models/business-search';
import { BusinessEntityType } from '../../../models/business-type-entities';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { PurchaseOrder } from '../../../models/purchase-order/purchase-order';
import { WorkOrderProxyService } from '../../../proxies/order/work-order.proxy.service';
import { FabricProxyService } from '../../../proxies/item/fabric.proxy.service';
import { YarnSearchProxyService } from '../../../proxies/item/yarn.proxy.service';
import { MatchingItemMasterCommand } from '../../../models/yarn/yarn-search';
import { DeliveryNote, ItemInDeliveryNote } from '../../../models/delivery-note/delivery-note';
import { WorkOrder, WorkOrderFabric, YarnCertificationDetails } from '../../../models/order/work-order';
import { finalize, forkJoin } from 'rxjs';

export interface ImportedBatchLot {
  providerLot: string;
  labelNumber: string;
  grossQuantity: number;
  netQuantity: number;
  manufacturedWeekAndYear?: string;
  itemManufacturedDate?: YearWeek;
  expiryDate?: Date;
  coneColor?: string;
  coneCount?: number;
  packagingType?: number;
}

@Component({
  selector: 'app-lots-add-modal',
  templateUrl: './lots-add-modal.component.html',
  styleUrl: './lots-add-modal.component.scss',
})
export class LotsAddModalComponent extends BaseModalComponent<Invoice | null> implements OnInit {
  @Input() invoices: Invoice[] | null = [];
  @Input() deliveryNotes: DeliveryNote[] = [];
  @Input() client: Business | null = null;
  @Input() isConsignment = false;

  batchImport = new FormControl<boolean>(true);

  selectedInvoice: FormControl<Invoice | null> = new FormControl<Invoice | null>(null);
  selectedPurchaseOrder: FormControl<string | null> = new FormControl<string | null>(null);
  selectedDeliveryNote: FormControl<string | null> = new FormControl<string | null>(null);
  selectedWorkOrder: FormControl<string | null> = new FormControl<string | null>(null);
  manufacturer: Business | null = null;
  deliveryNoteClient: Business | null = null;
  invoiceOptions: Invoice[] = [];
  purchaseOrderOptions: { label: string; value: string }[] = [];
  deliveryNoteOptions: DeliveryNote[] = [];
  workOrderOptions: { label: string; value: string }[] = [];
  fileUploaded = false;
  itemCode = new FormControl<string>('', Validators.required);
  selectedItem: ItemMaster | null = null;
  numberLotsArray: FormArray<FormGroup> = new FormArray<FormGroup>([]);
  lotsFormGroup: FormGroup = new FormGroup({
    lots: this.numberLotsArray,
  });
  blockButtonSave = false;
  packagingType: EnumValues = {};
  uomControl = new FormControl<number>(0);
  uoms: EnumValues = {};
  manufacturerOverride: Business | null = null;
  uomOptions: { label: string; value: string }[] = [];
  manufacturerOptions: Business[] = [];
  overrideManufacturer = new FormControl<boolean>(false);
  itemSuggestions: ItemMaster[] = [];
  items: ItemMaster[] = [];
  constructor(
    injector: Injector,
    private lotService: LotsService,
    private enumService: EnumService,
    private itemMasterProxyService: ItemMasterProxyService,
    private businessProxyService: BusinessProxyService,
    private cdr: ChangeDetectorRef,
    private localeHelperService: LocaleHelperService,
    private shipmentProxyService: ShipmentProxyService,
    private workOrderProxyService: WorkOrderProxyService,
    private fabricProxyService: FabricProxyService,
    private yarnSearchProxyService: YarnSearchProxyService,
    private inventoryLotProxyService: InventoryLotProxyService,
  ) {
    super(injector);
    effect(() => {
      this.packagingType = this.enumService.getEnum('PackagingType', 'inventoryEnums').values;
      const uomEnum = this.enumService.getEnum('UnitOfMeasureType', 'inventoryEnums').values;
      this.uoms = uomEnum;
      this.uomOptions = Object.entries(uomEnum).map(([key, value]) => ({
        label: value,
        value: key,
      }));
    });
  }

  ngOnInit(): void {
    // Every invoice / PO / delivery note / work order change resets the item code, and a preview is only valid for its context.
    this.itemCode.valueChanges.subscribe(() => this.clearImport());

    // Initialize manufacturer as null
    if (!this.isConsignment) {
      this.manufacturer = null;
    }

    if (this.isConsignment && this.client) {
      this.deliveryNoteClient = this.client;
    }

    if (this.isConsignment) {
      // For consignment mode, initialize delivery note options
      this.deliveryNoteOptions = this.deliveryNotes;

      // Listen to delivery note changes
      this.selectedDeliveryNote.valueChanges.subscribe((deliveryNoteId) => {
        // Reset manufacturer, work order and item when delivery note changes
        this.manufacturer = null;
        this.deliveryNoteClient = null;
        this.selectedWorkOrder.reset();
        this.itemCode.reset();

        // Find the full delivery note object by ID
        if (deliveryNoteId) {
          const deliveryNote = this.deliveryNotes.find((dn: DeliveryNote) => dn.id === deliveryNoteId);

          if (deliveryNote) {
            // Load client information from delivery note
            if (deliveryNote.client) {
              this.deliveryNoteClient = deliveryNote.client;
            } else if (deliveryNote.clientReference) {
              // Load client from API if only reference is available
              this.businessProxyService.getById(deliveryNote.clientReference).subscribe({
                next: (response) => {
                  this.deliveryNoteClient = response.data;
                  this.cdr.detectChanges();
                },
                error: (error) => {
                  console.error('Error loading client:', error);
                  this.deliveryNoteClient = null;
                  this.cdr.detectChanges();
                },
              });
            }

            this.loadWorkOrderOptions(deliveryNote);
          } else {
            this.workOrderOptions = [];
          }
        } else {
          this.workOrderOptions = [];
        }

        this.cdr.detectChanges();
      });

      // Listen to work order changes to reset item selection and set editing lot
      this.selectedWorkOrder.valueChanges.subscribe((woRef) => {
        this.itemCode.reset();
        this.manufacturer = null;

        // Set editing delivery note lot when both DN and WO are selected
        if (woRef && this.selectedDeliveryNote.value) {
          const deliveryNoteId = this.selectedDeliveryNote.value;
          const deliveryNote = this.deliveryNotes.find((dn) => dn.id === deliveryNoteId);

          if (deliveryNote) {
            this.lotService.setEditingDeliveryNoteLot(deliveryNoteId, woRef);

            // Load items for the selected work order from delivery note
            this.loadItemsForWorkOrder(deliveryNote, woRef);
          }
        } else {
          this.items = [];
        }

        this.cdr.detectChanges();
      });

      // Listen to itemCode changes in consignment mode to update selectedItem
      this.itemCode.valueChanges.subscribe((selectedItemCode) => {
        if (selectedItemCode) {
          this.selectedItem = this.items.find((item) => item.itemCode === selectedItemCode) || null;
        } else {
          this.selectedItem = null;
        }
      });
    } else {
      // Standard mode with invoices
      this.selectedInvoice.valueChanges.subscribe((invoice) => {
        // Reset manufacturer and purchase order when invoice changes
        this.manufacturer = null;
        this.selectedPurchaseOrder.reset();
        this.itemCode.reset();

        // Load purchase order options
        if (invoice) {
          this.loadPurchaseOrderOptions(invoice);
        } else {
          this.purchaseOrderOptions = [];
        }

        this.cdr.detectChanges();
      });

      // Listen to purchase order changes to reset item selection and set editing lot
      this.selectedPurchaseOrder.valueChanges.subscribe((poRef) => {
        this.itemCode.reset();
        this.manufacturer = null;

        // Set editing invoice lot when both invoice and PO are selected
        if (poRef && this.selectedInvoice.value?.id) {
          this.lotService.setEditingInvoiceLot(this.selectedInvoice.value.id, poRef);
        }

        this.cdr.detectChanges();
      });

      // Listen to itemCode changes to load manufacturer for selected item
      this.itemCode.valueChanges.subscribe((selectedItemCode) => {
        if (selectedItemCode && this.selectedInvoice.value) {
          this.loadManufacturerForItem(selectedItemCode);
        } else {
          this.manufacturer = null;
          this.cdr.detectChanges();
        }
      });

      const distinctItemCodes: string[] = (
        _.uniq(this.invoices?.flatMap((invoice) => invoice.purchaseOrders!.flatMap((po) => po.items.map((item) => item.itemCode)))) || []
      ).map((c) => String(c));

      const itemCodesBatchCommand = {
        itemCodes: distinctItemCodes,
      };

      this.itemMasterProxyService.getItemCodesBatch(itemCodesBatchCommand).subscribe({
        next: (results) => {
          this.items = results.data || [];
        },
      });
    }
  }

  public autoCompleteItem(event: AutoCompleteCompleteEvent) {
    const term = event.query;
    const command: ItemSearchCommand = {
      itemCode: term,
      name: term,
    };
    this.itemMasterProxyService.postItemSearch(command, true).subscribe({
      next: (results) => {
        this.itemSuggestions =
          results.data?.items?.map((item) => ({
            ...item,
            fieldToDisplay: `(${item.itemCode}) ${this.localeHelperService.getLocalizedValue(item.itemLongDisplayName)}`,
          })) || [];
      },
      error: () => {
        this.itemSuggestions = [];
      },
    });
  }

  onClose() {
    this.close({ action: 'close', data: null });
    this.lotService.cancelEditing();
    this.invoices = null;
    this.deliveryNotes = [];
  }

  getItemOptions(invoiceReference: string, purchaseOrderReference: string) {
    const invoice = this.invoices?.find((inv) => inv.id === invoiceReference);

    if (!invoice) return [];

    const purchaseOrder = invoice.purchaseOrders!.find((po) => po.id === purchaseOrderReference);

    const items = this.items.filter((item) => purchaseOrder!.items.some((poItem) => poItem.itemCode === item.itemCode));

    return items.map((item) => ({
      label: '(' + item.itemCode + ') ' + this.localeHelperService.getLocalizedValue(item?.itemDescription),
      value: item.itemCode,
    }));
  }

  getItemOptionsForDeliveryNote() {
    // Items are already filtered by loadItemsForWorkOrder, just format them
    if (!this.items || this.items.length === 0) {
      return [];
    }

    // For delivery notes, items are now ItemMaster with itemCode property
    return this.items.map((item: ItemMaster) => ({
      label: '(' + item.itemCode + ') ' + this.localeHelperService.getLocalizedValue(item?.itemDescription),
      value: item.itemCode,
    }));
  }

  getItemCode(itemInPurchaseOrderId: string, purchaseOrderReference: string): string {
    const purchaseOrder = this.selectedInvoice.value?.purchaseOrders!.find((po) => po.id === purchaseOrderReference);
    const itemCode = purchaseOrder?.items.find((i) => i.id === itemInPurchaseOrderId)?.itemCode;
    return itemCode !== undefined && itemCode !== null ? String(itemCode) : '';
  }
  getUomForItem(itemId: string, purchaseOrderReference: string): string {
    const purchaseOrder = this.selectedInvoice.value?.purchaseOrders!.find((po) => po.id === purchaseOrderReference);
    const uom = purchaseOrder?.items.find((i) => i.id === itemId)?.uom;
    return uom !== undefined && uom !== null ? String(uom) : '';
  }

  selectItem(event: AutoCompleteSelectEvent) {
    this.selectedItem = event.value as ItemMaster;
  }

  getUomForSelectedItem(): string {
    if (!this.selectedItem) return '';
    return this.uoms[this.selectedItem.unitOfMeasure];
  }

  getUomForItemCode(itemId: string): string {
    const uom = this.items.find((item) => item.itemCode === itemId)?.unitOfMeasure;
    return uom !== undefined && uom !== null ? this.uoms[uom] : '';
  }

  get canSelectItem(): boolean {
    return !_.isNil(this.selectedInvoice.value) && !_.isNil(this.selectedPurchaseOrder.value);
  }

  get isFormInvalid(): boolean {
    const baseValidation = this.blockButtonSave || this.itemCode.invalid || !this.fileUploaded || this.isImporting;

    // For non-consignment mode, also require purchase order selection
    if (!this.isConsignment) {
      return baseValidation || !this.selectedPurchaseOrder.value;
    }

    return baseValidation;
  }

  public selectInvoice(event?: Event, invoiceId?: string) {
    if (_.isNil(invoiceId) && _.isNil(event)) return;

    // Note: setEditingInvoiceLot is now called when purchase order is selected
    // in the selectedPurchaseOrder.valueChanges subscription
  }

  public calculateLots(quantityItem: number, units: number): number {
    return Math.ceil(quantityItem / units);
  }

  importedData: ImportedBatchLot[] = [];
  errors: { rowNumber?: number; message: string }[] = [];
  isImporting = false;

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.handleFile(input.files?.[0] ?? null);
    // Allows uploading the same file again after fixing it.
    input.value = '';
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  private handleFile(file: File | null): void {
    this.clearImport();
    if (!file) {
      this.errors = [{ message: 'No file selected.' }];
      return;
    }

    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      this.errors = [{ message: `Only .xlsx files are supported (received "${file.name}"). Download the template and save it as .xlsx.` }];
      return;
    }

    const context = this.buildImportContext();
    if (!context) {
      return;
    }

    // The API reads and validates the file (ExpiryDate must be an Excel Date cell); API errors are shown by the ErrorInterceptor.
    this.isImporting = true;
    this.inventoryLotProxyService
      .previewExcelImport(file, context)
      .pipe(
        finalize(() => {
          this.isImporting = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.applyImportPreview(response.data);
          }
        },
      });
  }

  private buildImportContext(): LotExcelImportContext | null {
    const itemCode = this.itemCode.value;
    if (!itemCode) {
      return null;
    }
    if (this.isConsignment) {
      return this.selectedDeliveryNote.value ? { itemCode, deliveryNoteReference: this.selectedDeliveryNote.value } : null;
    }
    const invoiceReference = this.selectedInvoice.value?.id;
    const purchaseOrderReference = this.selectedPurchaseOrder.value;
    return invoiceReference && purchaseOrderReference ? { itemCode, invoiceReference, purchaseOrderReference } : null;
  }

  private applyImportPreview(preview: LotExcelImportPreview): void {
    this.importedData = preview.rows
      .filter((row) => row.isValid)
      .map((row) => ({
        providerLot: row.providerLot ?? '',
        labelNumber: row.labelNumber ?? '',
        grossQuantity: row.grossQuantity ?? 0,
        netQuantity: row.netQuantity ?? 0,
        manufacturedWeekAndYear: row.manufacturedWeekAndYear,
        itemManufacturedDate: row.itemManufacturedDate,
        expiryDate: row.expiryDate ? new Date(row.expiryDate) : undefined,
        packagingType: row.packagingType,
      }));
    this.errors = preview.rows
      .filter((row) => !row.isValid)
      .map((row) => ({
        rowNumber: row.rowNumber,
        message: row.errors.map((error) => this.localeHelperService.getLocalizedValue(error)).join(' '),
      }));
    this.fileUploaded = true;

    if (preview.totalErrors > 0) {
      GlobalMessageUtil.warn(
        'Import Warning',
        `${preview.totalErrors} row(s) have errors and will not be added. Check the list of errors.`,
      );
    }
  }

  private clearImport(): void {
    this.importedData = [];
    this.errors = [];
    this.fileUploaded = false;
  }

  public invoiceTypeAhead(event: AutoCompleteCompleteEvent) {
    const term = event.query;
    if (term === '') {
      this.invoiceOptions = this.invoices || [];
    }

    this.invoiceOptions = this.invoices?.filter((invoice) => invoice.invoiceNumber!.toLowerCase().includes(term.toLowerCase())) || [];
  }

  manufacturerAutoComplete(event: AutoCompleteCompleteEvent) {
    const query = event.query.toLowerCase();
    const searchCommand: BusinessSearchCommand = {
      code: query,
      name: query,
      businessTypes: [BusinessEntityType.Manufacturer],
    };
    this.businessProxyService.search(searchCommand, true).subscribe({
      next: (results) => {
        this.manufacturerOptions = results.data?.items || [];
      },
      error: () => {
        this.manufacturerOptions = [];
      },
    });
  }

  public getPurchaseOrderById(purchaseOrderId: string): PurchaseOrder | undefined {
    return this.selectedInvoice.value?.purchaseOrders!.find((po) => po.id === purchaseOrderId);
  }

  private loadPurchaseOrderOptions(invoice: Invoice): void {
    this.purchaseOrderOptions = [];

    // Get unique purchase order references from invoice items
    this.purchaseOrderOptions =
      invoice
        .purchaseOrders!.filter((po) => po.id !== undefined)
        .map((po) => ({
          label: po.purchaseOrderNumber!,
          value: po.id!,
        })) || [];
  }

  private loadWorkOrderOptions(deliveryNote: DeliveryNote): void {
    this.workOrderOptions = [];

    // Check if workOrders array is present and populated
    if (deliveryNote.workOrders && deliveryNote.workOrders.length > 0) {
      // Use existing workOrders
      this.workOrderOptions = deliveryNote.workOrders
        .filter((wo: WorkOrder) => wo && wo.id)
        .map((wo: WorkOrder) => ({
          label: wo.orderNumber || wo.id,
          value: wo.id,
        }));
    } else if (deliveryNote.workOrderReferences && deliveryNote.workOrderReferences.length > 0) {
      // Load work orders from API using IDs with expand=true to get fabrics

      const workOrderCalls = deliveryNote.workOrderReferences.map((woId: string) =>
        this.workOrderProxyService.getWorkOrderById(woId, true),
      );

      if (workOrderCalls.length > 0) {
        forkJoin(workOrderCalls).subscribe({
          next: (responses) => {
            const workOrders = responses.filter((response) => response.success && response.data).map((response) => response.data!);

            // Store the loaded work orders back in the delivery note for future use
            deliveryNote.workOrders = workOrders;

            this.workOrderOptions = workOrders.map((wo: WorkOrder) => ({
              label: wo.orderNumber || wo.id,
              value: wo.id,
            }));

            this.cdr.detectChanges();
          },
          error: (error) => {
            console.error('Error loading work orders:', error);
            this.workOrderOptions = [];
            this.cdr.detectChanges();
          },
        });
      }
    }
  }

  private loadItemsForWorkOrder(deliveryNote: DeliveryNote, workOrderReference: string): void {
    // Get only items from delivery note that belong to this work order
    const deliveryNoteItems =
      deliveryNote.items?.filter((dnItem: ItemInDeliveryNote) => {
        return dnItem.workOrderReference === workOrderReference;
      }) || [];

    if (deliveryNoteItems.length === 0) {
      this.items = [];
      return;
    }

    // Find the work order to get fabric details
    const workOrder = deliveryNote.workOrders?.find((wo: WorkOrder) => wo.id === workOrderReference);

    if (!workOrder) {
      this.items = [];
      return;
    }

    // Use ALL fabrics from the work order (don't filter by delivery note items)
    const allFabrics: WorkOrderFabric[] = workOrder.fabrics || [];

    if (allFabrics.length === 0) {
      this.items = [];
      return;
    }

    // Extract yarn codes from yarnCertificationDetails of ALL fabrics
    const yarnCommands: MatchingItemMasterCommand[] = [];
    allFabrics.forEach((fabric: WorkOrderFabric) => {
      if (fabric.yarnCertificationDetails && fabric.yarnCertificationDetails.length > 0) {
        fabric.yarnCertificationDetails.forEach((yarnDetail: YarnCertificationDetails) => {
          if (yarnDetail.yarnCode) {
            yarnCommands.push({
              yarnCode: yarnDetail.yarnCode,
              certificationReference: yarnDetail.certificationReferences,
            });
          }
        });
      }
    });

    if (yarnCommands.length === 0) {
      this.items = [];
      return;
    }

    // Load ItemMaster using yarn codes
    this.yarnSearchProxyService.getItemMastersByYarnCode(yarnCommands).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          // The response is a Record<string, ItemMaster | null>
          const itemMasterMap: Record<string, ItemMaster | null> = response.data;

          // Extract non-null ItemMaster values and remove duplicates by itemCode
          const itemMasters: ItemMaster[] = _.uniqBy(
            Object.values(itemMasterMap).filter((item): item is ItemMaster => item !== null),
            'itemCode',
          );

          this.items = itemMasters;
        } else {
          this.items = [];
        }
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Error loading ItemMasters:', error);
        this.items = [];
        this.cdr.detectChanges();
      },
    });
  }

  public save() {
    if (this.batchImport.value && this.importedData.length > 0 && !_.isEmpty(this.itemCode.value)) {
      if (!this.isConsignment && !this.selectedInvoice.value) {
        GlobalMessageUtil.error('Error', 'Invoice selection is required for non-consignment lots.');
        return;
      }
      if (this.isConsignment && !this.selectedDeliveryNote.value) {
        GlobalMessageUtil.error('Error', 'Delivery Note selection is required for consignment lots.');
        return;
      }

      const lastLost = _.last(
        this.isConsignment
          ? (this.lotService.editingDeliveryNoteLot?.localLots ?? this.lotService.lotItemsForConsignment)
          : (this.lotService.editingInvoiceLot?.localLots ?? this.lotService.lotItemsForConsignment),
      );
      let lotIdNumber = lastLost && lastLost.id ? parseInt(lastLost.id.split('-')[1]) + 1 : 1;

      // Find ItemMaster from items array using itemCode for both modes
      const itemMaster: ItemMaster | null = this.items.find((item) => item.itemCode === this.itemCode.value) || null;

      if (!itemMaster) {
        GlobalMessageUtil.error('Error', 'Selected Item Code is invalid.');
        return;
      }

      let manufacturerReference = this.manufacturer?.id ?? '';

      if (this.isConsignment && !_.isNil(this.deliveryNoteClient)) {
        manufacturerReference = this.overrideManufacturer.value ? (this.manufacturerOverride?.id ?? '') : this.deliveryNoteClient.id;
      }

      if (_.isNil(manufacturerReference) || manufacturerReference === '') {
        GlobalMessageUtil.error('Error', 'Manufacturer reference is required.');
        return;
      }

      const purchaseOrder = this.selectedInvoice.value?.purchaseOrders!.find((po) => po.id === this.selectedPurchaseOrder.value);
      const deliveryNote = this.deliveryNotes?.find((dn: DeliveryNote) => dn.id === this.selectedDeliveryNote.value);

      // Find work order from workOrders array
      const workOrder = deliveryNote?.workOrders?.find((wo: WorkOrder) => wo.id === this.selectedWorkOrder.value);

      const newLots = _.map(this.importedData, (lot) => {
        const newLot: LotItem = {
          number: `Local-${lotIdNumber}`,
          consumptionEntries: [],
          itemCode: itemMaster.itemCode,
          item: itemMaster,
          netQuantity: lot.netQuantity,
          grossQuantity: lot.grossQuantity,
          originalGrossQuantity: lot.grossQuantity,
          originalNetQuantity: lot.netQuantity,
          invoiceReference: !this.isConsignment ? this.selectedInvoice.value?.id : undefined,
          purchaseOrderReference: !this.isConsignment ? purchaseOrder?.id : undefined,
          purchaseOrder: !this.isConsignment ? this.getPurchaseOrderById(purchaseOrder?.id ?? '') : undefined,
          deliveryNoteReference: this.isConsignment ? deliveryNote?.id : undefined,
          workOrderReference: this.isConsignment ? workOrder?.id : undefined,
          providerReference: !this.isConsignment ? purchaseOrder?.providerReference : undefined,
          originReference: !this.isConsignment ? purchaseOrder?.providerReference : undefined,
          manufacturerReference: manufacturerReference,
          clientReference: this.isConsignment ? this.deliveryNoteClient?.id : undefined,
          inbound: !this.isConsignment,
          notes: [],
          providerLotId: String(lot.providerLot).trim(),
          providerLabelNumber: String(lot.labelNumber).trim(),
          local: true,
          isActive: true,
          itemManufacturedDate: lot.itemManufacturedDate,
          expiryDate: lot.expiryDate,

          type: purchaseOrder?.type === 2 ? 2 : 0,
          packagingType: lot.packagingType ?? 0,
          yarnLotItemDetails:
            lot.coneColor || lot.coneCount
              ? {
                  coneColor: lot.coneColor ?? '',
                  coneCount: lot.coneCount ?? 0,
                }
              : undefined,
        };
        lotIdNumber++;
        return newLot;
      });
      this.lotService.batchAddLots(newLots);

      this.close({ action: 'save', data: this.lotsFormGroup.value });
    } else {
      GlobalMessageUtil.error('Error', 'No data to import or Item Code is missing.');
    }
  }

  private loadManufacturerForItem(itemCode: string): void {
    if (!this.selectedInvoice.value || !itemCode || !this.selectedPurchaseOrder.value) {
      this.manufacturer = null;
      this.cdr.detectChanges();
      return;
    }

    const invoice = this.selectedInvoice.value;
    const selectedPoId = this.selectedPurchaseOrder.value;

    // Get the selected purchase order
    const purchaseOrder = invoice.purchaseOrders?.find((po) => po.id === selectedPoId);

    if (!purchaseOrder) {
      this.manufacturer = null;
      this.cdr.detectChanges();
      return;
    }

    // Get the purchase order item that matches the selected item code
    const purchaseOrderItem = purchaseOrder.items.find((poItem) => poItem.itemCode === itemCode);

    if (!purchaseOrderItem) {
      this.manufacturer = null;
      this.cdr.detectChanges();
      return;
    }

    // Find the corresponding invoice item using itemInPurchaseOrderId and purchaseOrderReference
    const invoiceItem = invoice.items.find(
      (item) => item.itemInPurchaseOrderId === purchaseOrderItem.id && item.purchaseOrderReference === selectedPoId,
    );

    if (invoiceItem?.manufacturerReference) {
      this.loadManufacturerDataById(invoiceItem.manufacturerReference);
    } else {
      this.manufacturer = null;
      this.cdr.detectChanges();
    }
  }

  private loadManufacturerDataById(manufacturerRef: string): void {
    if (!manufacturerRef) {
      this.manufacturer = null;
      this.cdr.detectChanges();
      return;
    }

    this.businessProxyService.getById(manufacturerRef).subscribe({
      next: (response) => {
        this.manufacturer = response.data;
        this.cdr.detectChanges();
      },
      error: () => {
        this.manufacturer = null;
        this.cdr.detectChanges();
      },
    });
  }

  get manufacturerItems(): ItemMaster[] {
    if (!this.manufacturer || !this.selectedInvoice.value || !this.selectedPurchaseOrder.value) {
      return [];
    }

    const invoice = this.selectedInvoice.value;
    const selectedPoId = this.selectedPurchaseOrder.value;

    // Get the selected purchase order
    const purchaseOrder = invoice.purchaseOrders?.find((po) => po.id === selectedPoId);

    if (!purchaseOrder) {
      return [];
    }

    // Get all invoice items that have the same manufacturerReference as the current manufacturer
    // and belong to the selected purchase order
    const manufacturerInvoiceItems = invoice.items.filter(
      (item) => item.manufacturerReference === this.manufacturer?.id && item.purchaseOrderReference === selectedPoId,
    );

    // Get the corresponding purchase order items
    const manufacturerPurchaseOrderItems = purchaseOrder.items.filter((poItem) =>
      manufacturerInvoiceItems.some((invItem) => invItem.itemInPurchaseOrderId === poItem.id),
    );

    // Filter items from the batch that match the manufacturer's purchase order items
    const items = this.items.filter((item) => manufacturerPurchaseOrderItems.some((poItem) => poItem.itemCode === item.itemCode));

    return items;
  }
}
